import React, { useState, useEffect } from 'react';
import { PlanDetail } from '../types';
import PlanCard from './PlanCard';
import PlanModal from './plans/PlanModal';
import AddonManagerModal from './plans/AddonManagerModal';
import BillingDiscountModal from './plans/BillingDiscountModal';
import PlansHeader from './plans/PlansHeader';
import CheckoutModal from './CheckoutModal';
import { api } from '../services/api';
import { useSettings } from './SettingsContext';

const Plans: React.FC = () => {
  const { settings } = useSettings();
  const [plans, setPlans] = useState<PlanDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'quarterly' | 'annually'>('monthly');
  const [selectedPlan, setSelectedPlan] = useState<PlanDetail | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PlanDetail | null>(null);
  const [isAddonManagerOpen, setIsAddonManagerOpen] = useState(false);
  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);

  const qDiscount = settings.checkoutConfig?.quarterlyDiscountPercentage ?? 0;
  const yDiscount = settings.checkoutConfig?.yearlyDiscountPercentage ?? 0;

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const data = await api.plans.getAll();
      setPlans(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleToggleStatus = async (id: string) => {
    await api.plans.toggleStatus(id);
    await fetchPlans();
  };

  const handleSavePlan = async (plan: PlanDetail) => {
    if (editingPlan) {
      await api.plans.update(plan.id, plan);
      setEditingPlan(null);
    } else {
      await api.plans.create(plan);
      setIsCreating(false);
    }
    await fetchPlans();
  };

  const handleDeletePlan = async (id: string) => {
    await api.plans.delete(id);
    setEditingPlan(null);
    await fetchPlans();
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-top-4 duration-500">
      <PlansHeader
        qDiscount={qDiscount}
        yDiscount={yDiscount}
        onOpenDiscountModal={() => setIsDiscountModalOpen(true)}
        onOpenAddonManager={() => setIsAddonManagerOpen(true)}
        onCreateTier={() => setIsCreating(true)}
      />

      <div className="flex items-center justify-center gap-4 py-3 bg-white/50 rounded-2xl border border-slate-100 backdrop-blur-sm">
        <button 
          onClick={() => setBillingCycle('monthly')}
          className={`text-xs font-bold transition-all px-4 py-1.5 rounded-lg ${billingCycle === 'monthly' ? 'text-indigo-600 bg-indigo-50 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
        >
          Monthly
        </button>
        <button 
          onClick={() => setBillingCycle('quarterly')}
          className={`flex items-center gap-2 text-xs font-bold transition-all px-4 py-1.5 rounded-lg ${billingCycle === 'quarterly' ? 'text-indigo-600 bg-indigo-50 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
        >
          <span>Quarterly</span>
          {qDiscount > 0 && (
            <span className="bg-blue-100 text-blue-700 text-[9px] font-black px-1.5 py-0.5 rounded-full uppercase tracking-tighter shadow-sm">
              -{qDiscount}%
            </span>
          )}
        </button>
        <button 
          onClick={() => setBillingCycle('annually')}
          className={`flex items-center gap-2 text-xs font-bold transition-all px-4 py-1.5 rounded-lg ${billingCycle === 'annually' ? 'text-indigo-600 bg-indigo-50 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
        >
          <span>Annual</span>
          {yDiscount > 0 && (
            <span className="bg-emerald-100 text-emerald-700 text-[9px] font-black px-1.5 py-0.5 rounded-full uppercase tracking-tighter shadow-sm">
              -{yDiscount}%
            </span>
          )}
        </button>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-32">
          <svg className="animate-spin h-8 w-8 text-indigo-600 mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-slate-400 text-xs font-black uppercase tracking-widest">Loading Pricing Tiers...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {plans.map(plan => (
            <PlanCard
              key={plan.id}
              plan={plan}
              billingCycle={billingCycle}
              onSelect={setSelectedPlan}
              onToggleStatus={handleToggleStatus}
              onEdit={setEditingPlan}
            />
          ))}
          <button
            onClick={() => setIsCreating(true)}
            className="flex flex-col items-center justify-center p-8 rounded-3xl border-2 border-dashed border-slate-200 bg-slate-50/50 hover:bg-white hover:border-indigo-300 transition-all group min-h-[420px]"
          >
            <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center border border-slate-200 shadow-sm mb-3 group-hover:scale-110 transition-transform group-hover:shadow-indigo-100">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-400 group-hover:text-indigo-600">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </div>
            <span className="text-slate-700 font-bold text-sm">Add New Workspace Tier</span>
            <p className="text-slate-400 text-xs text-center mt-1 px-4">Create a plan with name, plan ID, price, and features.</p>
          </button>
        </div>
      )}

      {isCreating && (
        <PlanModal
          onClose={() => setIsCreating(false)}
          onSave={handleSavePlan}
        />
      )}

      {editingPlan && (
        <PlanModal
          initialPlan={editingPlan}
          onClose={() => setEditingPlan(null)}
          onSave={handleSavePlan}
          onDelete={handleDeletePlan}
        />
      )}

      {isAddonManagerOpen && (
        <AddonManagerModal onClose={() => setIsAddonManagerOpen(false)} />
      )}

      {isDiscountModalOpen && (
        <BillingDiscountModal onClose={() => setIsDiscountModalOpen(false)} />
      )}

      {selectedPlan && (
        <CheckoutModal
          plan={selectedPlan}
          billingCycle={billingCycle}
          onClose={() => setSelectedPlan(null)}
          onSuccess={() => setSelectedPlan(null)}
        />
      )}
    </div>
  );
};

export default Plans;
